'use client';

import { Link, usePathname } from '@ci/i18n/navigation';
import { SidebarMenuButton, SidebarMenuItem } from '@ci/ui/sidebar';
import { cn, isRouteActive } from '@ci/ui/utils';

/**
 * The link itself, WITHOUT the `<li>` wrapper.
 *
 * A collapsible section needs this piece on its own: its list item already
 * comes from the `Collapsible` root (`render={<SidebarMenuItem />}` in
 * `DocsNavigationCollapsible`), so wrapping it again would nest an `<li>`
 * inside an `<li>` and drop the row out of the list for screen readers.
 * `DocsNavLink` below is this plus the wrapper, for every ordinary page.
 */
export function DocsNavLinkButton({
  label,
  url,
  children,
}: React.PropsWithChildren<{ label: string; url: string }>) {
  const currentPath = usePathname();
  const isCurrent = isRouteActive(url, currentPath);

  return (
    <SidebarMenuButton
      render={<Link href={url} />}
      isActive={isCurrent}
      className={cn('text-sidebar-foreground/80 transition-all')}
    >
      <span className="block max-w-full truncate">{label}</span>

      {children}
    </SidebarMenuButton>
  );
}

export function DocsNavLink({
  label,
  url,
  children,
}: React.PropsWithChildren<{ label: string; url: string }>) {
  return (
    <SidebarMenuItem>
      <DocsNavLinkButton label={label} url={url}>
        {children}
      </DocsNavLinkButton>
    </SidebarMenuItem>
  );
}
